import { Trans } from "react-i18next";
import React, { Component, ErrorInfo, ReactNode } from "react";
import { errorLoggingService } from "../services/errorLoggingService";
import "./GlobalErrorBoundary.css";
import logger from "@shared/utils/logger";

const CHUNK_RELOAD_TS_KEY = "nervedx_chunk_reload_ts";
const CHUNK_RELOAD_WINDOW_MS = 15000;

function isChunkLoadError(error: Error): boolean {
  const message = String(error?.message || "").toLowerCase();
  return (
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("importing a module script failed") ||
    message.includes("chunkloaderror")
  );
}

/** React DOM races after Vite HMR / bootstrap overlay leave orphaned trees. */
function isRecoverableDomMutationError(error: Error): boolean {
  const message = String(error?.message || "").toLowerCase();
  const name = String(error?.name || "").toLowerCase();
  return (
    name === "notfounderror" ||
    message.includes("failed to execute 'removechild'") ||
    message.includes("the node to be removed is not a child") ||
    message.includes("failed to execute 'insertbefore'")
  );
}

function shouldReloadForChunkError(): boolean {
  try {
    const now = Date.now();
    const previousRaw = sessionStorage.getItem(CHUNK_RELOAD_TS_KEY);
    const previous = previousRaw ? Number(previousRaw) : 0;
    if (Number.isFinite(previous) && previous > 0 && now - previous < CHUNK_RELOAD_WINDOW_MS) {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_TS_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (
      (isChunkLoadError(error) || isRecoverableDomMutationError(error)) &&
      shouldReloadForChunkError()
    ) {
      window.location.replace(
        `${window.location.pathname}${window.location.search || ""}${window.location.hash || ""}`,
      );
      return;
    }

    // Log error to console in development
    if (import.meta.env.DEV) {
      logger.error("Uncaught error:", error);
      logger.error("Error info:", errorInfo);
    }

    // Send to error logging service (Sentry, backend audit log, etc.)
    errorLoggingService.logError(error, {
      feature: "Global",
      componentStack: errorInfo.componentStack ?? undefined,
      url: window.location.href,
      additionalData: {
        errorBoundary: "GlobalErrorBoundary",
        severity: "critical",
      },
    });

    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Optionally reload the page
    window.location.href = "/dashboard";
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="container mt-5">
          <div className="card border-danger">
            <div className="card-header bg-danger text-white">
              <h4 className="mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <Trans ns="features" i18nKey="ui.copy.8d886c0ba6f2" /></h4>
            </div>
            <div className="card-body">
              <p className="mb-3">
                <Trans ns="features" i18nKey="ui.copy.0d7ae1772652" /></p>

              {import.meta.env.DEV && this.state.error && (
                <div className="alert alert-warning">
                  <strong><Trans ns="features" i18nKey="ui.copy.ea61ff3b269e" /></strong>
                  <pre className="mt-2 mb-0 error-boundary-pre">
                    {this.state.error.toString()}
                  </pre>
                  {this.state.errorInfo && (
                    <details className="mt-2">
                      <summary><Trans ns="features" i18nKey="ui.copy.5ad2a3946eb9" /></summary>
                      <pre className="error-boundary-stack">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={this.handleReset}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  <Trans ns="features" i18nKey="ui.copy.cdac261089e0" /></button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => window.location.reload()}
                >
                  <i className="bi bi-arrow-repeat me-2"></i>
                  <Trans ns="features" i18nKey="ui.copy.6e41ee3f309a" /></button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
